import { useState, useEffect } from "react";
import {
  Typography,
  Button,
  IconButton,
  Paper,
  Grid,
  Box,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../../utils/ApiService";

export default function BankProductsPage() {
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const [bankProducts, setBankProducts] = useState([]);
  const [bankTab, setBankTab] = useState([]);
  const [yaazhBranches, setYaazhBranches] = useState([]);
  const [isBankProductsChanged, setIsBankProductsChanged] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const bankProducts = user.bankProducts || [];
    const bankTab = user.bank_tab || [];
    setBankProducts(bankProducts);
    setBankTab(bankTab);
    setYaazhBranches(user.branch_tab || []);
  }, []);

  const getYaazhBranchName = (branchId) =>
    yaazhBranches.find((b) => b.id === branchId)?.branch || "";

  const bankProductColumns = [
    {
      field: "bank_name",
      headerName: "Bank Name",
      flex: 1,
      editable: true,
      renderEditCell: (params) => (
        <Select
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          fullWidth
        >
          {bankTab?.map((item) => (
            <MenuItem key={item?.id} value={item?.bank_name}>
              {item?.bank_name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    { field: "product_name", headerName: "Product Name", flex: 1, editable: true },
    {
      field: "yaazh_branch_id",
      headerName: "Yaazh Branch",
      flex: 1,
      editable: true,
      renderCell: (params) => getYaazhBranchName(params.value),
      renderEditCell: (params) => (
        <Select
          value={params.value ?? ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value === "" ? null : e.target.value,
            })
          }
          displayEmpty
          fullWidth
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {yaazhBranches?.map((item) => (
            <MenuItem key={item?.id} value={item?.id}>
              {item?.branch}
            </MenuItem>
          ))}
        </Select>
      ),
    },

    // 🆕 Policy column
    {
      field: "policy",
      headerName: "Policy",
      flex: 2,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
        />
      ),
    },

    {
      field: "template_path",
      headerName: "Template",
      flex: 1,
      editable: true,
      renderEditCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Button variant="contained" component="label" size="small">
            Upload
            <input
              type="file"
              accept=".docx"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                params.api.setEditCellValue(
                  { id: params.id, field: "template_path", value: file },
                  e
                );
                setTimeout(() => {
                  apiRef.current.stopRowEditMode({ id: params.id });
                }, 100);
              }}
            />
          </Button>
          {params.value?.name && (
            <Typography variant="body2" noWrap>
              {params.value.name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDeleteBankProduct(params.id)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const handleAddBankProduct = () => {
    const defaultBank = bankTab[0]?.bank_name || "";
    setBankProducts((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        bank_name: defaultBank,
        product_name: "",
        yaazh_branch_id: null,
        policy: "", // 🆕 Default empty
        template_path: null,
      },
    ]);
    setIsBankProductsChanged(true);
  };

  const handleSaveBankProducts = async () => {
    try {
      const formData = new FormData();
      const metadata = [];

      bankProducts.forEach((product) => {
        const isNew = typeof product.id === "string" && product.id.startsWith("temp");

        const payload = {
          id: isNew ? null : product.id,
          bank_Id: bankTab.find((b) => b.bank_name === product.bank_name)?.id || null,
          bank_name: product.bank_name,
          product_name: product.product_name,
          yaazh_branch_id: product.yaazh_branch_id ?? null,
          policy: product.policy || "", // 🆕 Send policy
          template_path: product.template_path?.name || product.template_path || null,
          file: product.template_path instanceof File ? product.template_path.name : null,
        };

        metadata.push(payload);

        if (product.template_path instanceof File) {
          formData.append("files", product.template_path);
        }
      });

      formData.append("data", JSON.stringify(metadata));

      const res = await ApiServices.post("user/create/bank-products", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.status === 200) {
        const updatedProducts = res?.data?.bankProducts || [];
        setBankProducts(updatedProducts);

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        user.bankProducts = updatedProducts;
        localStorage.setItem("user", JSON.stringify(user));

        enqueueSnackbar("Bank products saved successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to save bank products!", { variant: "error" });
      }
    } catch (error) {
      console.error("Error saving bank products:", error);
      enqueueSnackbar("Error saving bank products.", { variant: "error" });
    } finally {
      setIsBankProductsChanged(false);
    }
  };

  const processBankProductUpdate = (newRow, oldRow) => {
    setBankProducts((prev) =>
      prev?.map((p) => (p.id === newRow.id ? { ...oldRow, ...newRow } : p))
    );
    setIsBankProductsChanged(true);
    return newRow;
  };

  const handleDeleteBankProduct = async (id) => {
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const userId = user.id || null;
      const request = { id, deleted_by: userId };

      const res = await ApiServices.post("user/delete/bank-products", request);

      if (res?.status === 200) {
        const updated = bankProducts?.filter((p) => p.id !== id);
        setBankProducts(updated);
        localStorage.setItem(
          "user",
          JSON.stringify({ ...user, bankProducts: updated })
        );

        enqueueSnackbar("Bank product deleted successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to delete bank product!", { variant: "error" });
      }
    } catch (error) {
      console.error("Error deleting bank product:", error);
      enqueueSnackbar("Error deleting bank product.", { variant: "error" });
    } finally {
      setIsBankProductsChanged(true);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Bank Products</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBankProduct}
          >
            Add Product
          </Button>
          <Button
            sx={{ ml: 2 }}
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveBankProducts}
            disabled={!isBankProductsChanged}
          >
            Save
          </Button>
        </Box>
      </Grid>

      <div style={{ height: 400, marginTop: 20 }}>
        <DataGrid
          apiRef={apiRef}
          rows={bankProducts}
          columns={bankProductColumns}
          editMode="row"
          processRowUpdate={processBankProductUpdate}
          pageSize={5}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
        />
      </div>
    </Paper>
  );
}
